import { useState } from 'react';
import { AlertCircle, Tag } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { useUpdateOptiBaySession } from '@/hooks/useOptiBaySessions';
import type { OptiBaySession } from '@/types/optibay';
import { cn } from '@/lib/utils';

interface OptiBaySymptomStepProps {
  session: OptiBaySession;
  onNext: () => void;
}

const SYMPTOM_TAGS = [
  'Intermittent',
  'Cold Start',
  'Under Load',
  'After Warm-up',
  'Noise',
  'Vibration',
  'Warning Light',
] as const;

export function OptiBaySymptomStep({ session, onNext }: OptiBaySymptomStepProps) {
  const updateSession = useUpdateOptiBaySession();
  const [techNotes, setTechNotes] = useState(session.technician_symptom_notes || '');
  const [selectedTags, setSelectedTags] = useState<string[]>(
    (session.technician_symptom_tags as string[] | null) || []
  );

  const toggleTag = (tag: string) => {
    setSelectedTags(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
  };

  const handleContinue = async () => {
    const hasChanges =
      techNotes.trim() !== (session.technician_symptom_notes || '') ||
      JSON.stringify(selectedTags) !== JSON.stringify((session.technician_symptom_tags as string[] | null) || []);

    if (hasChanges) {
      await updateSession.mutateAsync({
        sessionId: session.id,
        updates: {
          technician_symptom_notes: techNotes.trim() || null,
          technician_symptom_tags: selectedTags.length > 0 ? selectedTags : null,
        } as any,
      });
    }
    onNext();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <AlertCircle className="h-4 w-4 text-orange-500" />
        <h3 className="font-semibold text-sm">Customer Symptom</h3>
      </div>

      {/* Original complaint — read-only */}
      {session.symptom_text ? (
        <div className="bg-muted/30 rounded-lg p-4">
          <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide mb-1">
            Original Customer Complaint
          </p>
          <p className="text-sm whitespace-pre-wrap">{session.symptom_text}</p>
          <p className="text-xs text-muted-foreground mt-2">
            Source: Workflow Complaints
          </p>
        </div>
      ) : (
        <div className="bg-muted/30 rounded-lg p-4 text-center">
          <p className="text-sm text-muted-foreground">
            No symptom recorded. Workflow complaints will appear here once added.
          </p>
        </div>
      )}

      {/* Technician symptom refinement */}
      <div className="space-y-2">
        <label className="text-xs font-medium block">Technician Notes (optional)</label>
        <Textarea
          placeholder="Add your observations, clarifications, or additional symptom details…"
          value={techNotes}
          onChange={e => setTechNotes(e.target.value)}
          className="min-h-[60px] text-xs"
        />
      </div>

      {/* Symptom tags */}
      <div className="space-y-2">
        <div className="flex items-center gap-1.5">
          <Tag className="h-3 w-3 text-muted-foreground" />
          <label className="text-xs font-medium">Symptom Tags (optional)</label>
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {SYMPTOM_TAGS.map(tag => (
            <button
              key={tag}
              type="button"
              onClick={() => toggleTag(tag)}
              className={cn(
                'text-xs px-2.5 py-1 rounded-full border font-medium transition-colors',
                selectedTags.includes(tag)
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border text-muted-foreground hover:bg-accent/50'
              )}
            >
              {tag}
            </button>
          ))}
        </div>
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleContinue}
          disabled={updateSession.isPending}
          className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
        >
          {updateSession.isPending ? 'Saving…' : 'Continue to System Family →'}
        </button>
      </div>
    </div>
  );
}
